import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { showErrorToast } from '../../../../../../common/global/helpers';
import Breadcrumbs from '../../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import { CardFormFactor, FormFactorSelectorContext } from '../../../../enums';
import { rewriteImageUrl } from '../../../../helpers';
import { EmployeeCardItemModel } from '../../../../models/Cards';
import { resources } from '../../../../resources/resources';
import { cardService } from '../../../../services/card';
import applicationCrumbs from '../../../ApplicationCrumbs';
import PlatformInformation from '../../../Common/PlatformInformation';
import MyAccountMenu from '../../Common/MyAccountMenu';
import { FormFactorSelector } from '../Common/FormFactorSelector';

export const ReissueCardPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [formFactor, setFormFactor] = useState<CardFormFactor | undefined>();
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [isSaveButtonLoading, setIsSaveButtonLoading] =
    useState<boolean>(false);

  const { state } = useLocation();
  const card = state.card as EmployeeCardItemModel;

  const reissueCard = async () => {
    if (!card || !formFactor) {
      return;
    }

    setIsSaveButtonLoading(true);
    const resp = await cardService.disableCard({
      cardId: card.employeeCardId,
      formFactorId: formFactor,
    });
    setIsSaveButtonLoading(false);

    if (!resp.hasErrors) {
      setShowSuccessDialog(true);
    } else {
      showErrorToast(
        translate(resources.employeeCards.reissueDialog.errorMessage),
      );
    }
  };

  if (!card) {
    return null;
  }

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.cards]}
      />
      <section className="section-main section-actions my-account-page my-cards-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel">
              <FormFactorSelector
                cardType={card.cardTypeId}
                selectedCardFormFactor={formFactor}
                onCardFormFactorChange={setFormFactor}
                onButtonClick={reissueCard}
                contextType={FormFactorSelectorContext.Reissue}
                isButtonLoading={isSaveButtonLoading}
              />
            </div>
          </div>
        </div>
      </section>
      {showSuccessDialog && <SuccessDialog card={card} />}
    </>
  );
};

interface SuccessDialogProps {
  card: EmployeeCardItemModel;
}

const SuccessDialog = ({ card }: SuccessDialogProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const closeDialog = () => navigate('/my-account/cards');

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      closeOnClickOutside
      className="dialog-m block-card-dialog"
    >
      <div className="dialog-image">
        <img src={rewriteImageUrl(card.mainImage, 122, 196)} />
      </div>
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.employeeCards.reissueDialog.successTitle)}
        </div>
        <div
          className="dialog-text centered"
          dangerouslySetInnerHTML={{
            __html: translate(
              resources.employeeCards.reissueDialog.successBody,
            ),
          }}
        />
      </div>
      <div className="dialog-actions vertical">
        <Button onClick={closeDialog} className="btn btn-outline">
          {translate(resources.understood)}
        </Button>
      </div>
    </Dialog>
  );
};
